import React from "react";
import { Box, Typography, Grid, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";
import { VideoPlayer } from "./HelpDialogItems";
import { InstructionItem } from "./functions";
import VideoGuideDialog from "./VideoGuideDialog";
import {
  lessonItems,
  quizItems,
  flashcardItems,
  textLabbetItems,
  gradingItems,
  powerpointItems,
  assistentItems,
  visionItems,
  imageItems,
  lessonPlanItems,
  podcastItems,
  hpItems,
} from "./HelpDialogItems";

import {
  podcastItemsStudent,
  quizItemsStudent,
  flashcardItemsStudent,
  assistentItemsStudent,
  gradingItemsStudent,
  imageItemsStudent,
  hpItemsStudent,
} from "./HelpDialogItemsStudents";

/**
 * HighlightItem component to display a single highlight
 * @param {Object} props
 * @param {Object} props.highlight - Highlight object with title, description, etc.
 * @param {number} props.index - Index of the highlight
 * @param {Function} props.onNavigate - Function to handle navigation
 */
const HighlightItem = ({ highlight, index, onNavigate }) => {
  const theme = useTheme();

  return (
    <Grid item xs={12} sm={6} md={6}>
      <Box
        data-highlight-index={index}
        sx={{
          height: "100%",
          borderRadius: 4,
          bgcolor: theme.palette.mode === "dark" ? "#283947" : "white",
          //boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
          animation: "fadeIn 0.3s ease forwards",
          backgroundImage: "none",
          overflow: "hidden",
          transition: "all 0.3s ease",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: 3,
          "@keyframes fadeIn": {
            "0%": { opacity: 0, transform: "translateY(10px)" },
            "100%": { opacity: 1, transform: "translateY(0)" },
          },
        }}
      >
        {/* Title */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            fontSize: "1.3rem",
            transition: "all 0.3s ease",
            textAlign: "center",
            mb: 3,
          }}
        >
          {highlight.title}
        </Typography>

        {/* Video */}
        <Box
          sx={{
            width: "100%",
            borderRadius: 2,
            overflow: "hidden",
            mb: 0,
            opacity: 0.85,
            transition: "opacity 0.3s ease",
            "&:hover": {
              opacity: 1,
            },
          }}
        >
          <VideoGuideDialog
            videoUrl={highlight.videoUrl}
            poster={highlight.poster}
            iconSize="2rem"
            autoPlay={true}
          />
        </Box>
        {/* Action Button */}
        <Button
          variant="purp"
          color="primary"
          endIcon={<ArrowForwardIcon />}
          onClick={() => onNavigate(highlight.path)}
          sx={{ fontWeight: 600, mt: "auto", display: "none" }}
        >
          Prova nu
        </Button>
      </Box>
    </Grid>
  );
};

/**
 * Highlights component to showcase multiple features with cards
 * @param {Object} props
 * @param {Array} props.highlights - Array of highlight objects
 * @param {Function} props.onActionClick - Optional callback for action button
 */
const Highlights = ({ onActionClick, role }) => {
  const navigate = useNavigate();

  // Function to find an item by title from multiple arrays
  const getHighlightsByTitles = (titles, itemArrays) => {
    const result = [];

    titles.forEach((title) => {
      for (const items of itemArrays) {
        const found = items.find((item) => item.title === title);
        if (found) {
          result.push(found);
          break;
        }
      }
    });

    return result;
  };

  // Usage in your component
  let highlights = [];

  if (role === "teacher") {
    const teacherTitles = [
      "Generera quiz & dela med elever",
      "Generera quiz & skriv ut",
      "Generera flashcards & dela med elever",
      "Generera lektion & dela med elever",
      "Generera prov & dela med elever",
      "Skapa en AI-assistent & dela med elever",
      "Träna din egen AI-assistent",
      "Bedöm elevtexter",
      "Generera PowerPoint",
      "Generera podcast",
      "Nutidsfrågor (1X2-frågor)",
      "Generera lektionsplanering & skriv ut",
    ];
    highlights = getHighlightsByTitles(teacherTitles, [
      quizItems,
      flashcardItems,
      textLabbetItems,
      gradingItems,
      powerpointItems,
      assistentItems,
      lessonItems,
      visionItems,
      imageItems,
      lessonPlanItems,
      podcastItems,
      hpItems,
    ]);
  } else if (role === "student") {
    const studentTitles = [
      "Generera quiz",
      "Generera flashcards",
      "Öva på högskoleprovet med AI",
      "Bedöm din text",
      "Generera podcast",
      "Träna din egen AI-assistent",
    ];
    highlights = getHighlightsByTitles(studentTitles, [
      podcastItemsStudent,
      quizItemsStudent,
      flashcardItemsStudent,
      assistentItemsStudent,
      gradingItemsStudent,
      imageItemsStudent,
      hpItemsStudent,
    ]);
  }

  if (!highlights || highlights.length === 0) {
    return null;
  }

  const handleNavigate = (path) => {
    if (onActionClick) {
      onActionClick(path);
    } else if (path) {
      navigate(path);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: { xs: "flex", md: "flex" },
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography
          variant="h4"
          component="h2"
          gutterBottom
          sx={{
            mb: 1,
            fontWeight: 600,
            textAlign: "center",
            width: "100%",
          }}
        >
          Utvalda funktioner
        </Typography>
      </Box>
      <Typography
        variant="subtitle1"
        sx={{
          color: "text.secondary",
          textAlign: "center",
          mb: 4,
          fontSize: "1.1rem",
        }}
      >
        Videos som visar hur du använder de populäraste funktionerna
      </Typography>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        {highlights.map((highlight, index) => (
          <HighlightItem
            key={index}
            highlight={highlight}
            index={index}
            onNavigate={handleNavigate}
          />
        ))}
      </Grid>
    </>
  );
};

export default Highlights;
